import React, { useMemo, useEffect, useState, Fragment } from 'react';
import { IoArrowRedoOutline } from 'react-icons/io5';
import { XIcon } from '@heroicons/react/solid';
import { FaLock, FaTrashAlt } from 'react-icons/fa';
import { useRouter } from 'next/router';
import { useTable, useSortBy, Column, ColumnInstance, UseSortByColumnProps } from 'react-table';

import useViewer from 'hooks/useViewer';
import { AuthStatus } from 'constants/auth';
import { LOGIN_PAGE, SIGNUP_PAGE } from 'constants/pages';
import { useGetOpcStrategiesForUserLazyQuery } from 'types/generated/client';

import Link from 'components/Link';
import Modal from 'components/Modal/HeadlessUiModal';
import TickerName from 'components/TickerName';
import PulseLoader from 'components/Loading/Pulse';

import { HeaderIcon, IconSortDesc, IconSortAsc } from 'components/Table';

import { SavedOpcStrategy } from './types';

type TickerType = {
  t: string;
  n: string;
  s: string;
  i: string;
};

interface ListStrategyProps {
  userId: string;
  isOpen: boolean;
  handleClose: () => void;
  expirationDate: string;
  tickerData: TickerType[];
  onStrategySelect: (strategy: SavedOpcStrategy) => void;
  handleDelete: (strategy: SavedOpcStrategy) => void;
}

const DEFAULT_TICKER = 'SPY';

const DEFAULT_COLUMNS = (
  tickerData: TickerType[],
  onStrategySelect: (strategy: SavedOpcStrategy) => void,
  handleDelete: (strategy: SavedOpcStrategy) => void,
): Column<SavedOpcStrategy & { numberOfLegs: number }>[] => [
    {
      Header: '',
      id: 'loadButton',
      Cell: ({ row }) => (
        <div className='flex items-center'>
          <button
            className="rounded bg-brand-primary px-2 py-1 text-xs text-white mr-2"
            onClick={() => {
              onStrategySelect(row.original);
            }}
          >
            Load
          </button>
          <FaTrashAlt className='text-red-500 cursor-pointer' onClick={() => handleDelete(row.original)} />
        </div>
      ),
      width: 60,
    },
    {
      Header: 'Name',
      accessor: 'title',
      Cell: ({ row, value }) => {
        return (
          <span className="capitalize">
            <Link
              href="javascript:void(0)"
              className="styled-font flex items-center underline"
              onClick={() => {
                onStrategySelect(row.original);
              }}
            >
              {value}
            </Link>
          </span>
        );
      },
      width: 300,
    },
    {
      Header: 'Ticker',
      accessor: 'ticker',
      width: 100,
      Cell: ({ value }) => {
        const tickerInfo = tickerData.find((t) => t.t === value);
        return (
          <div>
            <TickerName name={tickerInfo?.n || ''}>{value}</TickerName>
          </div>
        );
      },
    },
    {
      Header: 'Legs',
      accessor: 'numberOfLegs',
    },
    {
      Header: 'Created',
      accessor: 'createdAt',
      Cell: ({ value }) => {
        return <div>{new Date(value).toLocaleString()}</div>;
      },
    },
    {
      Header: 'Expiration Date',
      accessor: 'expiration',
    },
    {
      Header: 'Notes',
      id: 'description',
      width: 60,
    },
  ];

export const ListStrategyModal: React.FC<ListStrategyProps> = ({
  userId,
  expirationDate,
  handleClose,
  isOpen,
  tickerData,
  onStrategySelect,
  handleDelete
}) => {
  const viewer = useViewer();
  const router = useRouter();

  const { ticker: urlTicker } = router.query;

  const ticker: string = router.isReady ? (urlTicker as string | undefined) || DEFAULT_TICKER : '';

  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);

  const isLocked = !viewer?.viewer?.uid;
  const currentPath = router.asPath;

  const toggleRow = (index: number) => {
    setExpandedRowIndex(expandedRowIndex === index ? null : index);
  };

  const columns = useMemo(
    () => DEFAULT_COLUMNS(tickerData, onStrategySelect, handleDelete),
    [tickerData, onStrategySelect, router, ticker, handleDelete],
  );

  const [getOpcStrategiesForUser, { data: opcStrategiesForUser, loading }] =
    useGetOpcStrategiesForUserLazyQuery({
      fetchPolicy: 'network-only',
    });

  const data = useMemo<SavedOpcStrategy[]>(() => {
    return (
      opcStrategiesForUser?.opc_saved_strategies.map((strategy) => {
        return {
          ...strategy,
          numberOfLegs: strategy.positions.length,
          createdAtTimestamp: new Date(strategy.createdAt).getTime(),
        };
      }) || []
    );
  }, [opcStrategiesForUser]);

  const { getTableBodyProps, headerGroups, rows, prepareRow } = useTable<SavedOpcStrategy>(
    {
      columns,
      data,
      initialState: {
        // @ts-ignore
        sortBy: [{ id: 'createdAt', desc: true }],
      },
      sortTypes: {
        // @ts-ignore
        createdAt: (rowA, rowB) => {
          const a = new Date(rowA.original.createdAt).getTime();
          const b = new Date(rowB.original.createdAt).getTime();
          return a < b ? -1 : a > b ? 1 : 0;
        },
        // @ts-ignore
        expiration: (rowA, rowB) => {
          const a = new Date(rowA.original.expiration).getTime();
          const b = new Date(rowB.original.expiration).getTime();
          return a < b ? -1 : a > b ? 1 : 0;
        },
      },
    },
    useSortBy,
  );

  useEffect(() => {
    if (isOpen) {
      getOpcStrategiesForUser({
        variables: {
          userId,
          expiration: expirationDate,
        },
      });
    }
  }, [isOpen, getOpcStrategiesForUser, userId, expirationDate]);

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      classNameMaxWidth={isLocked ? '' : 'max-w-7xl'}
    >
      <div className="relative rounded-lg bg-palette-black-1 p-3">
        <button onClick={() => handleClose()} className="absolute right-3 top-2">
          <XIcon className="w-5" />
        </button>
        <div className="h-full p-3">
          {isLocked ? (
            <div className="flex justify-center">
              <FaLock className="mr-2" />
            </div>
          ) : null}
          {viewer.status !== AuthStatus.User ? (
            <div className="text-center text-lg font-semibold">Saved strategies locked</div>
          ) : (
            <div className="flex text-2xl font-semibold">Saved Strategies</div>
          )}
          <div className="mt-2">
            {viewer.status !== AuthStatus.User ? (
              <>
                <div className="text-pretty text-center text-lg font-normal text-brand-green-primary">
                  Create a free account or
                  <Link href={`${LOGIN_PAGE}?next=${currentPath}`} className="px-1 underline">
                    login
                  </Link>
                  to save your strategy.
                </div>
                <div className="flex justify-center gap-2 py-4">
                  <Link
                    role="button"
                    href={`${SIGNUP_PAGE}?next=${currentPath}`}
                    className="flex items-center justify-center rounded-md  border border-transparent bg-brand-primary px-8 py-2 text-base font-medium text-white no-underline"
                  >
                    <span className="mr-2">Get Free Access</span>
                    <IoArrowRedoOutline />
                  </Link>
                </div>
              </>
            ) : (
              <div className="mt-4">
                <div className="flex h-full w-full flex-auto flex-col overflow-x-auto overflow-y-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    {loading ? (
                      <div className="flex w-full justify-center">
                        <PulseLoader />
                      </div>
                    ) : (
                      <table className="min-w-full">
                        <thead className="sticky top-0 z-10 bg-palette-black-4">
                          {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                              {headerGroup.headers.map((column) => {
                                const sortableColumn =
                                  column as unknown as ColumnInstance<SavedOpcStrategy> &
                                  UseSortByColumnProps<SavedOpcStrategy>;

                                const { key, ...rest } = sortableColumn.getHeaderProps(
                                  sortableColumn.getSortByToggleProps(),
                                );

                                return (
                                  <th
                                    key={key}
                                    {...rest}
                                    scope="col"
                                    className="sticky top-0 z-10 cursor-pointer py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-white sm:pl-0"
                                  >
                                    <div className="flex items-center px-3">
                                      {column.render('Header')}
                                      {sortableColumn.isSorted && (
                                        <HeaderIcon>
                                          {sortableColumn.isSortedDesc ? (
                                            <IconSortDesc />
                                          ) : (
                                            <IconSortAsc />
                                          )}
                                        </HeaderIcon>
                                      )}
                                    </div>
                                  </th>
                                );
                              })}
                            </tr>
                          ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                          {rows.map((row, index) => {
                            prepareRow(row);
                            const { key, ...rowProps } = row.getRowProps();
                            const hasDescription = !!row.original.description;
                            return (
                              <Fragment key={key}>
                                <tr {...rowProps} className="border-b border-palette-border">
                                  {row.cells.map((cell) => {
                                    const { key, ...cellProps } = cell.getCellProps();
                                    return (
                                      <td
                                        key={key}
                                        {...cellProps}
                                        className="px-3 py-2 text-xs leading-5"
                                        style={{ width: `${cell.column.width}px` }}
                                      >
                                        {cell.render('Cell')}
                                        {cell.column.id === 'description' && (
                                          <button
                                            onClick={() => toggleRow(index)}
                                            className={`flex items-center rounded border ${hasDescription
                                              ? 'border-button-color bg-transparent hover:bg-button-color'
                                              : 'cursor-not-allowed border-gray-600 bg-palette-black-1 text-gray-500'
                                              } px-2 py-1 transition-colors duration-150`}
                                            disabled={!hasDescription}
                                          >
                                            {expandedRowIndex === index ? 'Hide' : 'View'}
                                          </button>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                                {expandedRowIndex === index && (
                                  <tr className="bg-palette-black-3 text-xs text-white">
                                    <td colSpan={columns.length} className="p-3">
                                      {row.original.description || 'No description available'}
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
